import React, { useState, useEffect, useRef, ReactNode } from 'react';
import NoSSR from "@/utils/no-ssr";
import Time from '@/utils/time';
import { DateInputProps } from "@mantine/dates";
import { ScDatePickerProps } from "@/components/sc-controls/form-controls/sc-control-interfaces";
import 'dayjs/locale/en';
import ScDateControl from "@/components/sc-controls/form-controls/v2/ScDateControl";


const useLegacy = false;

function SCDatePicker(inputProps: ScDatePickerProps) {

    const { name, value, label, error, hint, required, disabled, changeHandler, extraClasses, cypress, minDate, maxDate, canClear = false, onChange, icon, className, withAsterisk, readOnly, ...otherDateInputProps } = inputProps;

    const datePickerProps: DateInputProps = {
        name,
        label,
        error,
        description: hint,
        required,
        disabled,
        minDate: minDate ? Time.parseDate(minDate) : minDate,
        maxDate: maxDate ? Time.parseDate(maxDate) : maxDate,
        rightSection: icon,
        className,
        withAsterisk,
        readOnly,
        ...otherDateInputProps
        // classNames: extraClasses
    };


    // A malformed/unparseable stored value (e.g. the "NaN-NaN-NaN NaN:NaN:NaN" string Time.toISOString
    // produces from an already-invalid Date) must never reach ScDateControl as a Date object: Mantine's
    // DateInput formats it internally via the native Date.prototype.toISOString(), which throws
    // "RangeError: Invalid time value" and crashes the whole tree with no error boundary to catch it.
    // `new Date(value)` alone doesn't guard against this - an Invalid Date is still a truthy object.
    const parseValidDate = (v: any): Date | null => (v && Time.isValidDate(Time.parseDate(v))) ? Time.parseDate(v) : null;

    const ref = useRef<HTMLInputElement>(null);
    const [val, setVal] = useState<Date | null>(parseValidDate(value));

    useEffect(() => {
        setVal(parseValidDate(value));
    }, [value]);

    const handleMantineDatePickerChange = (e: Date | null) => {
        setVal(e);
        changeHandler && changeHandler({ target: ref.current, name: name, value: e ? Time.toISOString(e) : null });
        onChange && onChange(e ? Time.toISOString(e) : null);
    }

    const onDatePickerChange = (e: any) => {
        if (e && e.value !== null) {
            let date = Time.parseDate(e.value);
            let valueTemp: any = value;
            if (!valueTemp) {
                valueTemp = Time.toISOString(Time.today(), true, true);
            }
            let dateValue = Time.updateDate(valueTemp, date);
            let stringValue = Time.toISOString(dateValue, true, true, true);
            setVal(dateValue);

            if (e.target.validity.valid && changeHandler) {
                changeHandler({ target: e.target.element, name: e.target.name, value: stringValue });
            }
        }
        else {
            changeHandler && changeHandler({ target: e.target.element, name: e.target.name, value: null });
        }
    };

    const rightSectionMantine = (): ReactNode => {
        return val && !disabled && canClear && <div style={{ display: "flex", pointerEvents: "none", position: "absolute" }}>
            <div style={{ paddingTop: "6px" }} >
                <>
                    <span style={{ pointerEvents: "all", cursor: "pointer" }}
                        onClick={() => handleMantineDatePickerChange(null)}>
                        <img src="/specno-icons/clear.svg" />
                    </span>
                </>
            </div>

            <style jsx>{`
                :global(.mantine-DatePicker-rightSection) {
                    width: 0px;
                    right: 16px;
                    background: red;
                }
            `}</style>
        </div>;
    }

    return (
        <NoSSR>
            <ScDateControl
                {...datePickerProps}
                value={val}
                onChange={handleMantineDatePickerChange}
                rightSection={rightSectionMantine()}
            />
        </NoSSR>
    );
}

export default SCDatePicker;
